import React, { useState, useEffect } from 'react';
import { Box, Grid, IconButton, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, MessageSquare, Check } from 'lucide-react';
import SolutionProduct from '../proposal/SolutionProduct.tsx';
import ProcessComparison from '../proposal/ProcessComparison.tsx';
import ProfitRiskAnalysis from '../proposal/ProfitRiskAnalysis.tsx';
import UseCaseComparison from '../proposal/UseCaseComparison.tsx';
// Import from both solutions data files to ensure we have all possible solutions
import { solutions as mockSolutions } from '../../data/mockData.ts';
import { solutions as extraSolutions } from '../../data/solutionsData.ts';
import FeedbackPanel from './FeedbackPanel.tsx';
import PaymentPanel from './PaymentPanel.tsx';
//import AttributionPanel from './AttributionPanel.tsx';

interface SolutionDetailContentProps {
  solutionId: string | null;
  onBack: () => void;
}

const ContentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  color: '#8a2be2',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const FeedbackButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  background: 'linear-gradient(45deg, #8A2BE2 30%, #4B0082 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #9932CC 30%, #8A2BE2 90%)',
  },
}));

const AcceptButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #00E676 30%, #00CC99 90%)',
  },
}));

const SolutionDetailContent: React.FC<SolutionDetailContentProps> = ({
  solutionId,
  onBack,
}) => {
  const [solution, setSolution] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  // Add state for solution status
  const [solutionStatus, setSolutionStatus] = useState<string>('proposed');
  // Add state for dialog
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<'feedback' | 'payment' | 'attribution' | null>(null);

  useEffect(() => {
    if (!solutionId) {
      setLoading(false);
      setError(true);
      return;
    }

    // Combine solutions from both data sources
    const allSolutions = [...mockSolutions, ...extraSolutions];
    
    // Find the solution by ID
    let foundSolution = allSolutions.find(sol => sol.id === solutionId);
    
    if (!foundSolution) {
      console.warn(`Solution with ID ${solutionId} not found`);
      setLoading(false);
      setError(true);
      return;
    }

    // Set the solution status if it exists
    if (foundSolution.status) {
      setSolutionStatus(foundSolution.status);
    }

    // Create a complete solution object with default values for any missing properties
    const completeSolution = {
      id: foundSolution.id,
      title: foundSolution.title || 'Untitled Solution',
      description: foundSolution.description || 'No description available',
      purpose: foundSolution.purpose || 'No purpose specified',
      status: foundSolution.status || 'proposed',
      
      // Scale metrics with defaults
      scaleMetrics: foundSolution.scaleMetrics || [
        { name: 'Volume', value: 5, color: '#4287f5' },
        { name: 'Velocity', value: 5, color: '#00CC99' },
        { name: 'Variety', value: 5, color: '#8A2BE2' },
        { name: 'Variability', value: 5, color: '#FFA500' },
        { name: 'Value', value: 5, color: '#FF4500' },
      ],
      
      // Success criteria with defaults
      successCriteria: foundSolution.successCriteria || [
        { id: 'default-1', description: 'No success criteria specified' }
      ],
      
      // Definition of done with defaults
      definitionOfDone: foundSolution.definitionOfDone || [
        { id: 'default-1', description: 'No definition of done specified' }
      ],
      
      // Current process with defaults
      currentProcess: {
        title: foundSolution.currentProcess?.title || 'Current Process',
        description: foundSolution.currentProcess?.description || 'No description available',
        steps: foundSolution.currentProcess?.steps || [
          { id: 'default-1', description: 'No steps defined for current process' }
        ]
      },
      
      // Future process with defaults
      futureProcess: {
        title: foundSolution.futureProcess?.title || 'Future Process',
        description: foundSolution.futureProcess?.description || 'No description available',
        steps: foundSolution.futureProcess?.steps || [
          { id: 'default-1', description: 'No steps defined for future process' }
        ]
      },
      
      // Financials with defaults
      financials: {
        current: foundSolution.financials?.current || [
          { id: 'default-1', name: 'No financial data available', amount: 0, type: 'risk' }
        ],
        future: foundSolution.financials?.future || [
          { id: 'default-1', name: 'No financial data available', amount: 0, type: 'profit' }
        ]
      },
      
      // Use cases with defaults
      useCases: {
        current: foundSolution.useCases?.current || [
          { id: 'default-1', title: 'No use cases defined', description: 'No current use cases available' }
        ],
        future: foundSolution.useCases?.future || [
          { id: 'default-1', title: 'No use cases defined', description: 'No future use cases available' }
        ]
      },
      
      // Add estimated cost for payment panel
      estimatedCost: foundSolution.estimatedCost || 10000
    };
    
    setSolution(completeSolution);
    setLoading(false);
  }, [solutionId]);

  // Transform financial data to match the expected format
  const transformFinancialData = (items: any[]) => {
    return items.map(item => ({
      id: item.id || `id-${Math.random()}`,
      description: item.name || item.description || 'No description',
      amount: typeof item.amount === 'number' ? item.amount : 0,
      type: item.type as 'profit' | 'risk'
    }));
  };

  // Add handlers for feedback and accept actions
  const handleFeedbackClick = () => {
    setDialogOpen(true);
    setActiveSection('feedback');
  };

  const handleAcceptClick = () => {
    setSolutionStatus('accepted');
    setDialogOpen(true);
    setActiveSection('payment');
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setActiveSection(null);
  };

  const handleSwitchSection = (section: 'feedback' | 'payment' | 'attribution') => {
    setActiveSection(section);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">Loading solution details...</Typography>
      </Box>
    );
  }

  if (error || !solution) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <BackButton onClick={onBack}>
          <ArrowLeft />
        </BackButton>
        <Typography variant="h6" sx={{ color: '#fff', mt: 3 }}>
          Solution not found
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          The requested solution could not be found or may have been deleted.
        </Typography>
        <Button 
          variant="contained" 
          onClick={onBack}
          sx={{ mt: 3 }}
        >
          Return to Solutions
        </Button>
      </Box>
    );
  }

  return (
    <ContentContainer>
      <BackButton onClick={onBack}>
        <ArrowLeft />
      </BackButton>

      <Typography variant="h4" sx={{ color: '#fff', mb: 2 }}>
        {solution.title}
      </Typography>
      
      {/* Status indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {solutionStatus === 'accepted' ? (
          <Chip 
            icon={<Check size={16} />} 
            label="Accepted" 
            sx={{ 
              background: 'rgba(0, 204, 153, 0.3)',
              color: '#00CC99',
              mr: 1
            }}
          />
        ) : (
          <Chip 
            label="Proposed" 
            sx={{ 
              background: 'rgba(138, 43, 226, 0.3)',
              color: '#8A2BE2',
              mr: 1
            }}
          />
        )}
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
          Estimated Cost: ${typeof solution.estimatedCost === 'number' ? solution.estimatedCost.toLocaleString() : '0'}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <ActionButtonsContainer>
        <FeedbackButton
          startIcon={<MessageSquare size={16} />}
          onClick={handleFeedbackClick}
        >
          Provide Feedback
        </FeedbackButton>
        
        {solutionStatus !== 'accepted' && (
          <AcceptButton
            startIcon={<Check size={16} />}
            onClick={handleAcceptClick}
          >
            Accept Solution
          </AcceptButton>
        )}
      </ActionButtonsContainer>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <SolutionProduct
            title={solution.title}
            description={solution.description}
            purpose={solution.purpose}
            scaleMetrics={solution.scaleMetrics}
            successCriteria={solution.successCriteria}
            definitionOfDone={solution.definitionOfDone}
          />
        </Grid>

        <Grid item xs={12}>
          <ProcessComparison
            currentTitle={solution.currentProcess.title}
            currentDescription={solution.currentProcess.description}
            currentSteps={solution.currentProcess.steps}
            futureTitle={solution.futureProcess.title}
            futureDescription={solution.futureProcess.description}
            futureSteps={solution.futureProcess.steps}
          />
        </Grid>

        <Grid item xs={12}>
          <ProfitRiskAnalysis
            currentItems={transformFinancialData(solution.financials.current)}
            futureItems={transformFinancialData(solution.financials.future)}
          />
        </Grid>

        <Grid item xs={12}>
          <UseCaseComparison
            currentUseCases={solution.useCases.current}
            futureUseCases={solution.useCases.future}
          />
        </Grid>
      </Grid>

      {/* Feedback/Payment/Attribution Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(138, 43, 226, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(138, 43, 226, 0.2)' }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            {activeSection === 'feedback' && 'Provide Feedback'}
            {activeSection === 'payment' && 'Process Payment'}
            {activeSection === 'attribution' && 'Attribution & Recognition'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', mb: 3 }}>
            <Button 
              variant={activeSection === 'feedback' ? 'contained' : 'outlined'}
              startIcon={<MessageSquare size={16} />}
              onClick={() => handleSwitchSection('feedback')}
              sx={{ 
                mr: 1,
                ...(activeSection === 'feedback' ? {
                  background: 'linear-gradient(45deg, #8A2BE2 30%, #4B0082 90%)',
                } : {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                  color: '#fff',
                })
              }}
            >
              Feedback
            </Button>
            
            <Button 
              variant={activeSection === 'payment' ? 'contained' : 'outlined'}
              startIcon={<Check size={16} />}
              onClick={() => handleSwitchSection('payment')}
              sx={{ 
                mr: 1,
                ...(activeSection === 'payment' ? {
                  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
                } : {
                  borderColor: 'rgba(0, 204, 153, 0.5)',
                  color: '#fff',
                })
              }}
            >
              Payment
            </Button>
          </Box>
          
          {activeSection === 'feedback' && (
            <FeedbackPanel proposalId={solution.id} />
          )}
          
          {activeSection === 'payment' && (
            <PaymentPanel proposalId={solution.id} solutions={[solution]} />
          )}
        </DialogContent>
        
        <DialogActions sx={{ borderTop: '1px solid rgba(138, 43, 226, 0.2)', p: 2 }}>
          <Button 
            onClick={handleDialogClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ContentContainer>
  );
};

export default SolutionDetailContent; 